import { useCallback, useReducer } from 'react';

const MAX_PAST = 80;

function clone(els) {
  return JSON.parse(JSON.stringify(els));
}

function init(initialElements) {
  return {
    elements: initialElements,
    past: [],
    future: [],
    gestureSnapshot: null,
  };
}

function reducer(state, action) {
  switch (action.type) {
    case 'set': {
      const next =
        typeof action.payload === 'function' ? action.payload(state.elements) : action.payload;
      if (JSON.stringify(state.elements) === JSON.stringify(next)) return state;
      const past = [...state.past, clone(state.elements)];
      if (past.length > MAX_PAST) past.shift();
      return { elements: next, past, future: [], gestureSnapshot: null };
    }
    case 'setDirect': {
      const next =
        typeof action.payload === 'function' ? action.payload(state.elements) : action.payload;
      if (JSON.stringify(state.elements) === JSON.stringify(next)) return state;
      return { ...state, elements: next };
    }
    case 'beginGesture': {
      return { ...state, gestureSnapshot: clone(state.elements) };
    }
    case 'endGesture': {
      const snap = state.gestureSnapshot;
      if (!snap) return state;
      if (JSON.stringify(snap) === JSON.stringify(state.elements)) {
        return { ...state, gestureSnapshot: null };
      }
      const past = [...state.past, snap];
      if (past.length > MAX_PAST) past.shift();
      return { ...state, past, future: [], gestureSnapshot: null };
    }
    case 'reset':
      return init(action.payload ?? []);
    case 'undo': {
      if (state.past.length === 0) return state;
      const top = state.past[state.past.length - 1];
      return {
        elements: top,
        past: state.past.slice(0, -1),
        future: [clone(state.elements), ...state.future],
        gestureSnapshot: null,
      };
    }
    case 'redo': {
      if (state.future.length === 0) return state;
      const [head, ...rest] = state.future;
      const past = [...state.past, clone(state.elements)];
      if (past.length > MAX_PAST) past.shift();
      return {
        elements: head,
        past,
        future: rest,
        gestureSnapshot: null,
      };
    }
    default:
      return state;
  }
}

export function useAnnotatorHistory(initial = []) {
  const [state, dispatch] = useReducer(reducer, initial, init);

  const setElements = useCallback((payload) => {
    dispatch({ type: 'set', payload });
  }, []);

  const setElementsDirect = useCallback((payload) => {
    dispatch({ type: 'setDirect', payload });
  }, []);

  const beginGesture = useCallback(() => {
    dispatch({ type: 'beginGesture' });
  }, []);

  const endGesture = useCallback(() => {
    dispatch({ type: 'endGesture' });
  }, []);

  const reset = useCallback((els) => {
    dispatch({ type: 'reset', payload: els });
  }, []);

  const undo = useCallback(() => dispatch({ type: 'undo' }), []);
  const redo = useCallback(() => dispatch({ type: 'redo' }), []);

  return {
    elements: state.elements,
    setElements,
    setElementsDirect,
    beginGesture,
    endGesture,
    reset,
    undo,
    redo,
    canUndo: state.past.length > 0,
    canRedo: state.future.length > 0,
  };
}
